import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { type TContentBlockBase } from '@onesource/schemas';
import { useCompleteBlock } from '@onesource/services';
import { useEffect, useRef } from 'react';

interface TextBlockContentProps {
  block: TContentBlockBase;
  lessonId: string;
  /**
   * Let the card fill its container instead of the default 900px reading width.
   * Used by the inline lesson template so the text card aligns with the video
   * and documents rows; the single-block page keeps the narrower reading width.
   */
  fullWidth?: boolean;
}

/**
 * Renders a single text content block (bodyHtml / body) with its progress
 * tracking. Shared by the single-block route page and the inline lesson
 * template so the renderer and tracking logic live in one place.
 *
 * View tracking (IN_PROGRESS) is gated behind an IntersectionObserver so that,
 * on the inline template, each block records a view only once when it first
 * scrolls into view rather than all blocks firing on page load.
 */
export function TextBlockContent({ block, fullWidth = false }: TextBlockContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { mutate: completeBlock } = useCompleteBlock();

  // Text is a view block: seeing it (scrolled into view once) completes it.
  const trackedRef = useRef(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry?.isIntersecting && !trackedRef.current) {
          trackedRef.current = true;
          completeBlock(block.id);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [block.id, completeBlock]);

  return (
    <Box ref={containerRef}>
      {/* Text Title */}
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        {block.title || 'Document'}
      </Typography>

      {/* Text Content */}
      <Box pb={4}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: fullWidth ? '100%' : 900,
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {block.bodyHtml ? (
            <Box
              dangerouslySetInnerHTML={{ __html: block.bodyHtml }}
              sx={{
                fontSize: '15px',
                lineHeight: 1.8,
                color: '#333',
                '& p': { mb: 2 },
                '& h1': { fontSize: '2rem', fontWeight: 'bold', mt: 4, mb: 2 },
                '& h2': { fontSize: '1.5rem', fontWeight: 'bold', mt: 3, mb: 2 },
                '& h3': { fontSize: '1.25rem', fontWeight: 'bold', mt: 3, mb: 1.5 },
                '& h4, & h5, & h6': { fontWeight: 'bold', mt: 2, mb: 1 },
                '& ul, & ol': { pl: 4, mb: 2 },
                '& li': { mb: 0.5 },
                '& a': { color: 'primary.main', textDecoration: 'underline' },
                '& blockquote': {
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  pl: 2,
                  ml: 0,
                  my: 2,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                },
                '& code': {
                  bgcolor: 'grey.100',
                  px: 0.5,
                  borderRadius: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.9em',
                },
                '& pre': {
                  bgcolor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  '& code': { bgcolor: 'transparent', px: 0 },
                },
                '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 2,
                  '& th, & td': { border: '1px solid', borderColor: 'divider', p: 1 },
                  '& th': { bgcolor: 'grey.50', fontWeight: 'bold' },
                },
              }}
            />
          ) : (
            <Typography
              variant="body1"
              sx={{ color: '#333', lineHeight: 1.8, fontSize: '15px', whiteSpace: 'pre-wrap' }}
            >
              {block.body || 'No content available.'}
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
